import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../../context/AppContext"
import { Icon, useT } from "../../shared"
import { useIsMobile } from "../../shared/useIsMobile"
import { useOrgName } from "../../shared/BrandLogo"
import { Btn, Segmented, LEVEL_COLORS } from "../../shared/components"
import { DonutChart, BarChart, LineChart, HBarChart } from "../../shared/charts"
import { analyticsSummary } from "../../api/users.api"
import { RANGE_OPTIONS, exportOverviewCsv } from "../CertAnalyticsScreens"
import { fmtDateShort, fmtCount } from "../../shared/format"
import { StatCard } from "./StatCard"
import { useDashboardOverview } from "./useDashboardOverview"
import { ApplicationsPanel } from "./ApplicationsPanel"
import { TasksTabPanel } from "./TasksTabPanel"
import { UpcomingSessionsCard } from "./UpcomingSessionsCard"
import { ProgramsOverviewCard } from "./ProgramsOverviewCard"
import { QuotaCards } from "./QuotaCards"
import { ManageTrainersModal } from "./ManageTrainersModal"

// Baris shortcut hero — kurasi khusus dashboard persisten (bukan seluruh
// adminActions 10-item dari WelcomeScreen, yang dibuat untuk onboarding
// sekali-jalan). Semua id nav diverifikasi ada di getNav('admin', ...).
function heroShortcuts(lang, navigate) {
  return [
    { icon: "upload", label: lang === "id" ? "Enroll Batch" : "Enroll Batch", to: "enrollment" },
    { icon: "calendar", label: lang === "id" ? "Buat Kelas Baru" : "New Class", to: "training" },
    { icon: "send", label: lang === "id" ? "Kirim Kode Klaim" : "Send Claim Codes", to: "cert-send" },
    { icon: "cert", label: lang === "id" ? "Kelola Sertifikat" : "Manage Certificates", to: "certificates" },
    { icon: "users", label: lang === "id" ? "Kelola Peserta" : "Manage Participants", to: "users" },
  ].map((s) => ({ ...s, action: () => navigate(s.to) }))
}

export function AdminDashboard() {
  const t = useT()
  const { lang, navigate, user, programs, currentProgram } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()

  const [summary, setSummary] = React.useState(null)
  const [manageTrainersOpen, setManageTrainersOpen] = React.useState(false)

  // currentProgram datang dari program switcher di sidebar. Kalau belum ada
  // yang terpilih (mis. tenant baru tanpa program), beri tahu admin alih-alih
  // membuka modal yang tidak tahu program mana yang dimaksud.
  const openManageTrainers = () => {
    if (!currentProgram) {
      toast.error(lang === "id" ? "Pilih program dulu dari bilah sisi" : "Pick a program from the sidebar first")
      return
    }
    setManageTrainersOpen(true)
  }

  // Satu panggilan untuk seluruh lembaga, bukan satu per program. Selain
  // lebih sedikit permintaan, ini juga yang membuat angkanya benar: dijumlah
  // di sisi klien, orang yang terdaftar di dua program terhitung dua kali.
  React.useEffect(() => {
    analyticsSummary().then(setSummary).catch(() => {})
  }, [])

  const totalParticipants = summary?.participants || 0
  const totalCertified = summary?.certified || 0
  const totalPending = summary?.certPending || 0
  const programsWithoutParticipants = summary?.programsWithoutParticipants || 0

  const overview = useDashboardOverview(lang)
  const { data } = overview

  const hour = new Date().getHours()
  const greeting = hour < 12
    ? (lang === "id" ? "Selamat pagi" : "Good morning")
    : hour < 17
      ? (lang === "id" ? "Selamat siang" : "Good afternoon")
      : (lang === "id" ? "Selamat malam" : "Good evening")
  const firstName = user?.fullName?.split(" ")[0] || (lang === "id" ? "Admin" : "Admin")

  const byLevel = data?.byLevel || []
  const timeline = data?.timeline || []
  const dropOff = data?.dropOff || []

  return (
    <div className="main-inner">
      {/* Greeting hero + shortcut */}
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{t.organization}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{greeting}, {firstName}</h1>
          <div className="muted mt-1">
            {fmtCount(programs.length, lang, "program", "program")} ·{" "}
            {fmtCount(totalParticipants, lang, "peserta", "participant")}
          </div>
        </div>
      </div>
      <div className="row gap-2 mb-4" style={{ flexWrap: "wrap" }}>
        {heroShortcuts(lang, navigate).map((s) => (
          <Btn key={s.to} variant="secondary" icon={<Icon name={s.icon} size={14} />} onClick={s.action}>
            {s.label}
          </Btn>
        ))}
      </div>

      {/* Kontrol range bersama — memicu SATU fetch analyticsOverview yang
          dipakai Card B/C/D sekaligus, bukan tiga filter per-kartu yang
          identik datanya (lihat plan: penyimpangan disengaja dari referensi). */}
      <div className="row between mb-3" style={{ alignItems: "center" }}>
        <div className="h3">{lang === "id" ? "Ringkasan" : "Overview"}</div>
        <Segmented
          value={overview.range}
          onChange={overview.setRange}
          options={RANGE_OPTIONS.map((o) => ({ value: o.value, label: lang === "id" ? o.id : o.en }))}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        <StatCard
          title={lang === "id" ? "Status Sertifikasi" : "Certification Status"}
          chart={
            <DonutChart
              value={totalParticipants ? Math.round((totalCertified / totalParticipants) * 100) : 0}
              centerText={`${totalCertified}/${totalParticipants}`}
            />
          }
          footnote={`${lang === "id" ? "Tersertifikasi" : "Certified"} ${totalCertified} · Pending ${totalPending}`}
          overflowActions={[
            { label: lang === "id" ? "Lihat detail sertifikat" : "View certificate detail", onClick: () => navigate("certificates") },
          ]}
        />
        <StatCard
          title={lang === "id" ? "Distribusi per Level" : "Distribution by Level"}
          chart={
            <BarChart
              data={byLevel.map((x) => ({ label: x.level == null ? "—" : `L${x.level}`, value: x.completionPct, color: LEVEL_COLORS[x.level] || "var(--text-3)" }))}
            />
          }
          legendItems={byLevel.map((x) => ({ label: x.level == null ? "—" : `Level ${x.level}`, color: LEVEL_COLORS[x.level] || "var(--text-3)" }))}
          overflowActions={[
            { label: lang === "id" ? "Lihat Analitik Progres" : "View Progress Analytics", onClick: () => navigate("progress") },
          ]}
        />
        <StatCard
          title={lang === "id" ? "Aktivitas" : "Activity"}
          chart={
            <LineChart
              lang={lang}
              height={160}
              formatX={(iso) => fmtDateShort(iso, lang)}
              series={[
                { name: lang === "id" ? "Nonton" : "Watch", points: timeline.map((p) => ({ x: p.date, y: p.watch })) },
                { name: "Test", points: timeline.map((p) => ({ x: p.date, y: p.test })) },
              ]}
            />
          }
          overflowActions={[
            { label: lang === "id" ? "Lihat Analitik Progres" : "View Progress Analytics", onClick: () => navigate("progress") },
          ]}
        />
        <StatCard
          title={lang === "id" ? "Top Drop-off" : "Top Drop-off"}
          chart={
            <HBarChart
              color="var(--danger)"
              valueSuffix="%"
              data={dropOff.map((l) => ({
                key: l.lessonId, label: l.title, title: `${l.moduleTitle} · ${l.title}`,
                value: l.dropOffPct, sub: `${l.moduleTitle} · ${l.startedCount} ${lang === "id" ? "penonton" : "viewers"}`,
              }))}
            />
          }
          overflowActions={[
            { label: lang === "id" ? "Ekspor CSV" : "Export CSV", onClick: () => data && exportOverviewCsv(data, lang, orgName) },
          ]}
        />
      </div>

      {/* Main + aside — pola dua kolom yang sama dipakai TrainerDashboard */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 16 }}>
        <div className="col gap-4">
          <TasksTabPanel
            programs={programs}
            programsWithoutParticipants={programsWithoutParticipants}
            certPending={totalPending}
          />
          <UpcomingSessionsCard programs={programs} />
          <ProgramsOverviewCard programs={programs} perProgram={summary?.perProgram} />
        </div>
        <div className="col gap-4">
          <ApplicationsPanel onManageTrainers={openManageTrainers} />
          <QuotaCards />
        </div>
      </div>

      {manageTrainersOpen && currentProgram && (
        <ManageTrainersModal
          program={currentProgram}
          onClose={() => setManageTrainersOpen(false)}
        />
      )}
    </div>
  )
}
